import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { ModelName, ReminderObjectType } from 'webapp/enums/enums';

import Reminder from 'webapp/models-custom/reminder';
import type Report from 'webapp/models-custom/report';
import type ReminderRecord from 'webapp/models/reminder';

import ModelManagerForStoreService from 'webapp/services/model-manager/for-store';

export type TrackingOriginTypeForReportReminderPopupOpen =
	'reports' | 'editor' | 'reports_scheduled' | 'home' | 'notifications';

export default class ModelManagerRemindersService extends ModelManagerForStoreService<
	ReminderRecord,
	Reminder
> {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	getAllModels() {
		return this.createModelsFromRecordsArray(this.savedRecords);
	}

	getForReport(reportId: number): Reminder[] {
		const reminderRecords = this.savedRecords.filter((record) => {
			return record.objectType === ReminderObjectType.REPORT && record.objectId === reportId;
		});
		return this.createModelsFromRecordsArray(reminderRecords);
	}

	createModelForReport(report: Report) {
		const model = this.createNewModel({
			objectType: ReminderObjectType.REPORT,
			objectId: report.numericIdForSure,
		});
		return model;
	}

	createAndEditForReport(report: Report, origin: string, isFromCreateChoicePopupFlow = false) {
		const model = this.createModelForReport(report);
		this.edit(model, origin, isFromCreateChoicePopupFlow);
	}

	edit(reminder: Reminder, trackingOrigin: string, isFromCreateChoicePopupFlow = false) {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.mmNotificationsService.openEditPopup(
			reminder,
			trackingOrigin,
			isFromCreateChoicePopupFlow,
		);
	}

	createAndEdit(trackingOrigin: TrackingOriginTypeForReportReminderPopupOpen) {
		const model = this.createNewModel({
			objectType: ReminderObjectType.REPORT,
		});
		this.edit(model, trackingOrigin);
	}

	createDuplicatedModel(model: Reminder) {
		const data = model.getDataForClone();
		return this.createNewModel(data);
	}

	// ------ Requests ------

	_remindersRequested = false;
	_remindersRequestPromise: Promise<void> | null = null;
	requestReminders(): Promise<void> {
		const variablePrefix = 'reminders';
		return this.requestService.dataRequestOnce<typeof variablePrefix>(
			this,
			variablePrefix,
			() => {
				return this.requestService.storeFindAll(ModelName.REMINDER);
			},
		);
	}

	// ------ For-store ------

	modelName = ModelName.REMINDER;

	createModel(model: ReminderRecord): Reminder {
		return new Reminder({ model });
	}
}

declare module '@ember/service' {
	interface Registry {
		'model-manager/reminders': ModelManagerRemindersService;
	}
}
